import { Box, Select, MenuItem } from "@mui/material";
import {
  People as PeopleIcon,
  Pending as PendingIcon,
  Event as EventIcon,
  AssignmentTurnedIn as CheckInSubmittedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { t } from "./coachTokens";

// `interactive` defaults to true so every existing caller (coach dashboard, super-admin overview)
// keeps its original pointer cursor + hover lift. Pass `interactive={false}` to render a read-only
// stat tile (no pointer, no hover) — used only by the company group admin's display-only overview.
// `ariaLabel` names the tile for assistive tech when the visible text isn't the whole story — e.g.
// the revenue tile, whose number is blurred until clicked, so the label carries the toggle's meaning.
export const KPI = ({ icon, iconBg, iconColor, badge, badgeBg, badgeColor, value, valueColor, label, sub, onClick, onBadgeClick, interactive = true, ariaLabel }) => (
  <Box
    onClick={onClick}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => {
      // A div-as-button still has to answer Enter/Space to be reachable without a mouse.
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); }
    } : undefined}
    aria-label={ariaLabel}
    sx={{
      background: t.white,
      border: `1px solid ${t.border}`,
      borderRadius: t.r,
      padding: "20px 22px",
      boxShadow: t.sh,
      cursor: interactive ? "pointer" : "default",
      transition: interactive ? "transform .18s cubic-bezier(.34,1.56,.64,1), box-shadow .18s" : "none",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      position: "relative",
      overflow: "hidden",
      "&:hover": interactive ? { transform: "translateY(-4px)", boxShadow: t.shHover } : {},
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "13px",
          background: iconBg,
          color: iconColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(15,20,40,.08)",
        }}
      >
        {icon}
      </Box>
      {badge && (
        typeof badge === "string" ? (
          <Box
            onClick={onBadgeClick ? (e) => { e.stopPropagation(); onBadgeClick(); } : undefined}
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              padding: "3px 9px",
              borderRadius: "20px",
              background: badgeBg,
              color: badgeColor,
              cursor: onBadgeClick ? "pointer" : "default",
              "&:hover": onBadgeClick ? { filter: "brightness(0.95)" } : {},
            }}
          >
            {badge}
          </Box>
        ) : (
          badge
        )
      )}
    </Box>
    <Box>
      <Box sx={{ fontSize: "30px", fontWeight: 800, lineHeight: 1, letterSpacing: "-.8px", color: valueColor }}>
        {value}
      </Box>
      <Box sx={{ fontSize: "12px", color: t.t2, marginTop: "4px", fontWeight: 500 }}>{label}</Box>
      {sub && <Box sx={{ fontSize: "11px", color: t.t3, marginTop: "2px" }}>{sub}</Box>}
    </Box>
  </Box>
);

const KpiRow = ({ stats, onSelect, expiringSoonDays = 30, onExpiringSoonDaysChange }) => {
  const navigate = useNavigate();
  const { totalTrainees, activeEnabled, activeTrainees, pendingApproval, expiringSoon, checkInsSubmittedThisWeek } = stats || {};
  const totalDisplay = totalTrainees == null ? "—" : totalTrainees.toLocaleString();
  const activeDisplay = activeTrainees == null ? null : activeTrainees.toLocaleString();
  const activeEnabledDisplay = activeEnabled == null ? null : `${activeEnabled.toLocaleString()} פעילים`;
  const pendingDisplay = pendingApproval == null ? "—" : pendingApproval.toLocaleString();
  const expiringDisplay = expiringSoon == null ? "—" : expiringSoon.toLocaleString();
  const checkInsSubmittedDisplay = checkInsSubmittedThisWeek == null ? "—" : checkInsSubmittedThisWeek.toLocaleString();
  const fire = (tab, state) => () => onSelect?.(tab, state);
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        gap: "14px",
        marginBottom: "18px",
      }}
    >
      <KPI
        icon={<PeopleIcon sx={{ fontSize: 22 }} />}
        iconBg={t.greenLight}
        iconColor={t.green}
        badge={activeDisplay ? `${activeDisplay} התאמנו השבוע` : null}
        badgeBg={t.greenLight}
        badgeColor={t.greenText}
        value={totalDisplay}
        valueColor={t.text}
        label="סה״כ מתאמנים"
        sub={activeEnabledDisplay}
        onClick={fire("clients", "ACTIVE")}
        onBadgeClick={activeDisplay ? fire("clients", "ACTIVE_THIS_WEEK") : null}
      />
      <KPI
        icon={<PendingIcon sx={{ fontSize: 22 }} />}
        iconBg={t.amberLight}
        iconColor={t.amber}
        badge="ממתינים לאישור"
        badgeBg={t.amberLight}
        badgeColor={t.amberText}
        value={pendingDisplay}
        valueColor={t.text}
        label="ממתינים לתוכנית"
        onClick={() => navigate("/app/program_renew")}
      />
      <KPI
        icon={<EventIcon sx={{ fontSize: 22 }} />}
        iconBg={t.redLight}
        iconColor={t.red}
        badge={
          <Select
            size="small"
            value={expiringSoonDays}
            onChange={(e) => onExpiringSoonDaysChange?.(Number(e.target.value))}
            onClick={(e) => e.stopPropagation()}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              background: t.redLight,
              color: t.redText,
              borderRadius: "20px",
              height: 24,
              "& .MuiSelect-select": { padding: "3px 22px 3px 9px !important", minHeight: "unset" },
              "& fieldset": { border: "none" },
              "& .MuiSelect-icon": { color: t.redText, right: 4, fontSize: 16 },
            }}
            MenuProps={{ PaperProps: { sx: { borderRadius: "10px" } } }}
          >
            <MenuItem value={7} sx={{ fontSize: 12 }}>7 ימים</MenuItem>
            <MenuItem value={14} sx={{ fontSize: 12 }}>14 ימים</MenuItem>
            <MenuItem value={30} sx={{ fontSize: 12 }}>30 ימים</MenuItem>
          </Select>
        }
        badgeBg="transparent"
        badgeColor={t.redText}
        value={expiringDisplay}
        valueColor={t.text}
        label="מנויים שמסתיימים"
        onClick={fire("clients", "CLOSE_TO_END")}
      />
      <KPI
        icon={<CheckInSubmittedIcon sx={{ fontSize: 22 }} />}
        iconBg={t.blueLight}
        iconColor={t.blue}
        badge="השבוע"
        badgeBg={t.blueLight}
        badgeColor={t.blueText}
        value={checkInsSubmittedDisplay}
        valueColor={t.text}
        label="צ׳ק-אינס הוגשו"
        onClick={() => navigate("/app/check-ins")}
      />
    </Box>
  );
};

export default KpiRow;
